import type { ReactNode } from "react";

import { cn } from "./lib/cn";

export interface SectionCardProps {
  title: string;
  description?: string;
  /**
   * Başlığın sağındaki "Tümünü gör ›" bağlantısı. `ReactNode` — `Card.actions` ile aynı
   * gerekçe: bazı çağıranlarda gerçek bir `<Link>` (yeni sekmede açma, prefetch), bazısında
   * bir `onClick` düğmesi olması gerekiyor; kit ikisine de zorlamıyor. Tutarlı görünüm için
   * `sectionCardLinkClasses` dışa aktarılır.
   */
  action?: ReactNode;
  children: ReactNode;
  /** Alttaki ortalı bağlantı (mockup: "Tüm sözleşmeleri görüntüle →"). */
  footer?: ReactNode;
  className?: string;
}

/** "Tümünü gör ›" / footer bağlantılarının ortak sınıfı — Link ya da button'a uygulanır. */
export const sectionCardLinkClasses = "text-[13px] font-medium text-accent hover:text-accent-hover";

/**
 * Bölüm kartı — UI Tur 2 §5. Mockup'ın en sık tekrarlanan üst-seviye kabı: başlık + isteğe
 * bağlı "Tümünü gör ›" + gövde + isteğe bağlı ortalı alt bağlantı.
 *
 * ⚠ `aria-label={title}` — kart bir `region` olarak duyurulur (Ana Sayfa'nın önceki
 * `Bolum` bileşeninin aynı deseni: `<section aria-label={baslik}>`). Görünür `<h2>` ZATEN
 * var; `aria-label` onu TEKRARLAMIYOR, `getByRole("region", {name})` ile bölüm arayan
 * testlerin çalışması için ekleniyor — birden çok ekran aynı kalıbı ("dört bölüm kanonik
 * sırada" gibi) region rolünden okuyor.
 */
export function SectionCard({
  title,
  description,
  action,
  children,
  footer,
  className,
}: SectionCardProps) {
  return (
    <section
      aria-label={title}
      className={cn("flex flex-col rounded-lg border border-border-default bg-surface", className)}
    >
      <header className="flex items-start justify-between gap-3 px-6 pt-6 pb-3">
        <hgroup className="min-w-0">
          <h2 className="text-base font-bold text-fg-heading">{title}</h2>
          {description ? <p className="mt-0.5 text-sm text-fg-muted">{description}</p> : null}
        </hgroup>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>
      <div className="px-6 pb-6">{children}</div>
      {footer ? (
        <footer className="border-t border-border-subtle px-6 py-4 text-center">{footer}</footer>
      ) : null}
    </section>
  );
}
